import {
  Store, CheckCircle, Gauge, TrendingUp, ShieldCheck, MapPin, Target,
  Building, ClipboardList, Star, Search, Package, BarChart3, Boxes, Award,
  Landmark, Briefcase, Users, DollarSign, Mail, Phone, CalendarDays, AtSign,
  Share2, MessageCircle, Funnel, Layout,
} from "lucide-react";
import type { ServiceDetail } from "@/components/sections/service-detail";

export const LOCAL_SEO_SERVICES: ServiceDetail[] = [
  {
    slug: "gmb-setup", title: "GMB Setup", eyebrow: "Google Business Profile",
    icon: Store,
    tagline: "Launch a complete, optimized Google Business Profile from day one.",
    description: "We build your Google Business Profile end-to-end — accurate categories, NAP, services, products, hours and rich media — so you start ranking the moment you're verified.",
    bullets: ["Business profile creation", "Correct primary & secondary categories", "Logo, cover and 10+ branded photos", "Service area & hours", "Initial Q&A seeded", "Services & products catalog"],
    deliverables: ["Live GBP listing", "Branded media pack", "Setup completion report"],
  },
  {
    slug: "gmb-verification", title: "GMB Verification", eyebrow: "Verified profile",
    icon: CheckCircle,
    tagline: "Postcard, video, phone or live-call — we get you verified.",
    description: "Verification is the #1 blocker for new GBP listings. We manage every method, document everything and rescue stuck profiles.",
    bullets: ["Postcard handling", "Video walk-through verification", "Phone & email verification", "Bulk verification for chains", "Stuck-verification rescue"],
    deliverables: ["Verified business profile", "Verification proof report"],
  },
  {
    slug: "gmb-optimization", title: "GMB Optimization", eyebrow: "Full optimization",
    icon: Gauge,
    tagline: "Every field, attribute, post, product and service tuned for visibility.",
    description: "We optimize every signal Google uses to rank local businesses — from your description and attributes to weekly posts and geo-tagged photos.",
    bullets: ["Keyword-rich description", "Service & product catalog", "Weekly Google Posts", "Photo geotagging", "LocalBusiness schema on website", "Attribute completion"],
    deliverables: ["Optimized GBP", "Schema markup on site", "Weekly post calendar"],
  },
  {
    slug: "gmb-ranking", title: "GMB Ranking", eyebrow: "Top 3 local pack",
    icon: TrendingUp,
    tagline: "Move from page 3 to the top 3 of the local pack.",
    description: "Monthly off-page actions, citations, reviews and behavioral signals that compound into top-3 local rankings.",
    bullets: ["Geo-grid rank tracking", "Citation building (100+)", "Review velocity campaigns", "Competitor gap analysis", "Monthly performance report"],
    deliverables: ["Monthly ranking report", "Geo-grid heatmaps", "Action log"],
  },
  {
    slug: "google-maps-seo", title: "Google Maps SEO", eyebrow: "Maps visibility",
    icon: MapPin,
    tagline: "Rank across every neighborhood, not just at your pin.",
    description: "Proximity is one of three ranking factors — but relevance and prominence can win you visibility blocks away. We build all three.",
    bullets: ["Geo-targeted optimization", "Neighborhood landing pages", "Driving directions optimization", "Geo-tagged content", "Maps embed strategy"],
    deliverables: ["Geo-grid visibility map", "Neighborhood pages", "Monthly maps report"],
  },
  {
    slug: "local-seo", title: "Local SEO", eyebrow: "Full local strategy",
    icon: Target,
    tagline: "Complete on-page + off-page local strategy tuned for local intent.",
    description: "A full Local SEO program covering your website, GBP, citations, reviews, content and links — built specifically to win local search.",
    bullets: ["Local keyword research", "Service-area landing pages", "Local schema markup", "Local link building", "Review strategy", "GBP management"],
    deliverables: ["Monthly strategy report", "Content & link calendar", "Ranking dashboard"],
  },
  {
    slug: "citation-building", title: "Citation Building", eyebrow: "NAP authority",
    icon: Building,
    tagline: "Accurate NAP citations on 100+ authority directories.",
    description: "Citations build trust and authority for your local business. We submit manually, fix inconsistencies and remove duplicates.",
    bullets: ["Manual submissions", "100+ authority directories", "NAP consistency audit", "Duplicate cleanup", "Industry-specific directories"],
    deliverables: ["Citation report (URL + login)", "NAP audit fixes", "Duplicate removal log"],
  },
  {
    slug: "local-listings", title: "Local Listings", eyebrow: "Cross-platform listings",
    icon: ClipboardList,
    tagline: "Apple Maps, Bing Places, Yelp, Facebook and niche directories.",
    description: "Beyond Google, customers discover you on dozens of platforms. We claim, verify and optimize every relevant local listing.",
    bullets: ["Apple Maps Connect", "Bing Places", "Yelp business page", "Facebook Business", "Niche industry sites"],
    deliverables: ["Per-platform completion report", "Unified branding", "Listing logins delivered"],
  },
  {
    slug: "map-pack-ranking", title: "Map Pack Ranking", eyebrow: "3-pack strategy",
    icon: MapPin,
    tagline: "Strategy specifically built for the 3-pack.",
    description: "The Map Pack drives 70%+ of local clicks. Our 3-pack program targets the exact signals Google uses to choose those three spots.",
    bullets: ["Map-pack keyword targeting", "Proximity & relevance optimization", "GBP behavioral signals", "Geo-tagged content clusters", "Spam fighting reports"],
    deliverables: ["3-pack heatmap", "Behavioral signal log", "Monthly ranking report"],
  },
  {
    slug: "google-reviews", title: "Google Reviews", eyebrow: "Review generation",
    icon: Star,
    tagline: "Ethical review generation funnels for happy customers.",
    description: "Compliant review funnels that route happy customers to Google and unhappy customers to a private feedback channel.",
    bullets: ["Branded review funnel", "QR code & link assets", "SMS/email request templates", "Review monitoring", "Response templates"],
    deliverables: ["Review funnel page", "Request templates", "Monthly review report"],
  },
  {
    slug: "reputation-management", title: "Reputation Management", eyebrow: "Brand protection",
    icon: ShieldCheck,
    tagline: "Monitor, respond and recover from negative reviews.",
    description: "We watch every review platform 24/7, respond on your behalf and run recovery campaigns for negative or unfair reviews.",
    bullets: ["24/7 monitoring", "Professional responses", "Negative review recovery", "Removal requests (TOS)", "Sentiment reporting"],
    deliverables: ["Monthly reputation report", "Response log", "Sentiment trend chart"],
  },
  {
    slug: "gmb-reinstatement", title: "GMB Reinstatement", eyebrow: "Suspension rescue",
    icon: ShieldCheck,
    tagline: "Recover suspended or disabled profiles fast.",
    description: "Suspended GBP? We diagnose the root cause, write the appeal and work with Google support until your profile is back live.",
    bullets: ["Suspension root cause analysis", "Appeal preparation", "Document & evidence gathering", "Google support escalation", "Post-reinstatement hardening"],
    deliverables: ["Reinstated profile", "Appeal documentation", "Prevention checklist"],
  },
];

export const AMAZON_SERVICES: ServiceDetail[] = [
  {
    slug: "amazon-seller-account", title: "Amazon Seller Account", eyebrow: "Account setup",
    icon: Store,
    tagline: "Professional Seller Central account creation, worldwide.",
    description: "We set up your Amazon Seller Central account in any marketplace, with all documents, tax info and bank verification handled.",
    bullets: ["Professional account creation", "Worldwide marketplaces", "Document preparation", "Tax & bank verification", "2-step verification setup"],
    deliverables: ["Live Seller Central account", "Account credentials", "Setup walkthrough"],
  },
  {
    slug: "amazon-approval", title: "Amazon Approval", eyebrow: "Gating & approvals",
    icon: CheckCircle,
    tagline: "Category, brand and gated-product approvals.",
    description: "Many categories require ungating before you can sell. We prepare invoices, applications and appeals to get you approved fast.",
    bullets: ["Category ungating", "Brand approval", "Gated product approval", "Invoice preparation", "Appeals when rejected"],
    deliverables: ["Approval confirmations", "Invoice templates", "Process documentation"],
  },
  {
    slug: "amazon-verification", title: "Amazon Verification", eyebrow: "Identity & video",
    icon: ShieldCheck,
    tagline: "Identity, address and video verification support.",
    description: "Amazon's verification process can stall accounts for weeks. We prep your documents, coach you for video calls and follow up daily.",
    bullets: ["Document preparation", "Video call coaching", "Address verification", "Identity verification", "Daily follow-up with Amazon"],
    deliverables: ["Verified account", "Document checklist", "Verification proof"],
  },
  {
    slug: "amazon-seo", title: "Amazon SEO", eyebrow: "Listing optimization",
    icon: Search,
    tagline: "Title, bullets, backend keywords and A+ content optimization.",
    description: "Amazon SEO is its own beast. We optimize every field — visible and hidden — to rank for the keywords that drive sales.",
    bullets: ["Title optimization", "Bullet points rewrite", "Backend search terms", "A+ content design", "Brand storefront optimization"],
    deliverables: ["Optimized listings", "A+ content pages", "Keyword ranking baseline"],
  },
  {
    slug: "product-listing", title: "Product Listing", eyebrow: "Conversion-first",
    icon: Package,
    tagline: "Conversion-focused listings with photography and copy.",
    description: "Premium product listings — high-conversion photography, persuasive copy, infographics and lifestyle imagery.",
    bullets: ["Product photography", "Lifestyle & infographics", "Persuasive copy", "Variation parent-child setup", "Bundle listings"],
    deliverables: ["Live product listings", "Image pack", "Copy documents"],
  },
  {
    slug: "keyword-research", title: "Keyword Research", eyebrow: "Helium 10 + JS",
    icon: Search,
    tagline: "Helium 10 + Jungle Scout deep keyword discovery.",
    description: "We mine 1000s of keywords, score them by search volume, competition and intent, and hand you a prioritized targeting list.",
    bullets: ["Helium 10 + Jungle Scout data", "Volume + competition scoring", "Long-tail discovery", "Competitor keyword gap", "Seasonality analysis"],
    deliverables: ["Prioritized keyword list", "Keyword tracking sheet", "Quarterly refresh"],
  },
  {
    slug: "keyword-ranking", title: "Keyword Ranking", eyebrow: "Page-1 strategy",
    icon: TrendingUp,
    tagline: "Strategic ranking to page-1 for high-intent terms.",
    description: "We combine PPC, organic SEO and external traffic to push your target keywords onto Amazon page 1 — and keep them there.",
    bullets: ["Launch ranking strategy", "PPC + organic blend", "External traffic funnels", "Honeymoon period optimization", "Ranking maintenance"],
    deliverables: ["Page-1 rankings", "Ranking dashboard", "Monthly review"],
  },
  {
    slug: "product-reviews", title: "Product Reviews", eyebrow: "TOS-compliant",
    icon: Star,
    tagline: "TOS-compliant review generation programs.",
    description: "Vine, Amazon-approved insert cards and follow-up email sequences — all within Amazon's TOS.",
    bullets: ["Amazon Vine enrollment", "Insert card design", "Follow-up email sequences", "Negative review monitoring", "Review velocity planning"],
    deliverables: ["Review program live", "Insert card files", "Monthly review report"],
  },
  {
    slug: "amazon-ppc", title: "Amazon PPC", eyebrow: "Sponsored Ads",
    icon: BarChart3,
    tagline: "Sponsored Products, Brands and Display campaign management.",
    description: "Daily-managed Amazon Ads campaigns focused on TACoS — total advertising cost of sales — not just ACoS vanity metrics.",
    bullets: ["Sponsored Products", "Sponsored Brands", "Sponsored Display", "Daily bid management", "TACoS-focused reporting"],
    deliverables: ["Live PPC campaigns", "Weekly optimization log", "Monthly performance report"],
  },
  {
    slug: "fba-setup", title: "FBA Setup", eyebrow: "Fulfillment",
    icon: Boxes,
    tagline: "Shipment plans, labels and FBA prep partner coordination.",
    description: "From your factory to Amazon's warehouses — we build shipment plans, FBA labels and coordinate with prep centers.",
    bullets: ["Shipment plan creation", "FNSKU labels", "Prep center coordination", "International freight setup", "Inventory placement"],
    deliverables: ["Shipment plans", "Label files", "Prep partner contacts"],
  },
  {
    slug: "brand-registry", title: "Brand Registry", eyebrow: "Brand protection",
    icon: Award,
    tagline: "Trademark + Brand Registry enrollment and protection.",
    description: "Brand Registry unlocks A+ content, storefronts and IP protection. We handle the trademark, enrollment and ongoing brand defense.",
    bullets: ["Trademark filing support", "Brand Registry enrollment", "IP infringement reports", "Hijacker monitoring", "Project Zero (if eligible)"],
    deliverables: ["Active Brand Registry", "Trademark docs", "Brand protection dashboard"],
  },
  {
    slug: "account-reinstatement", title: "Account Reinstatement", eyebrow: "POA experts",
    icon: Landmark,
    tagline: "Plan of action for suspended accounts and ASINs.",
    description: "Suspended Amazon account or ASIN? Our POA specialists write Amazon-approved appeals with root-cause analysis and preventive measures.",
    bullets: ["POA writing (account or ASIN)", "Root-cause analysis", "Evidence gathering", "Escalation handling", "Post-reinstatement hardening"],
    deliverables: ["Reinstated account / ASIN", "POA documentation", "Prevention plan"],
  },
];

export const LEAD_GEN_SERVICES: ServiceDetail[] = [
  {
    slug: "b2b-leads", title: "B2B Leads", eyebrow: "Decision-makers",
    icon: Briefcase,
    tagline: "Decision-maker leads from your ICP, verified and enriched.",
    description: "Targeted B2B leads filtered by industry, title, company size and tech stack — verified, enriched and ready for outreach.",
    bullets: ["ICP definition workshop", "Email + phone verification", "Tech stack enrichment", "LinkedIn URL included", "CRM-ready CSV"],
    deliverables: ["Verified lead list", "Enrichment data", "CRM integration"],
  },
  {
    slug: "b2c-leads", title: "B2C Leads", eyebrow: "Consumer reach",
    icon: Users,
    tagline: "High-volume consumer leads tuned to your offer.",
    description: "B2C lead campaigns built around your offer — demographics, geo, interests — delivered in real-time or batched.",
    bullets: ["Demographic targeting", "Geo-targeted leads", "Real-time delivery", "Compliance-checked sources", "Email + phone included"],
    deliverables: ["Real-time lead feed", "Lead quality report", "Replacement guarantee"],
  },
  {
    slug: "sales-leads", title: "Sales Leads", eyebrow: "Bottom-of-funnel",
    icon: DollarSign,
    tagline: "Bottom-of-funnel leads ready for a sales call.",
    description: "Pre-qualified sales leads that have shown buying intent — book demos, close deals, hit quota.",
    bullets: ["Intent-based filtering", "BANT-qualified", "Direct dial included", "Booking link integration", "Replacement for bad leads"],
    deliverables: ["Qualified sales leads", "Quality dashboard", "Weekly review"],
  },
  {
    slug: "targeted-leads", title: "Targeted Leads", eyebrow: "Hyper-targeted",
    icon: Target,
    tagline: "Hyper-targeted by industry, geo, title and intent.",
    description: "Build a list of exactly who you want — by industry, role, location, company revenue and signals like recent funding or hiring.",
    bullets: ["20+ filters", "Intent signals (hiring, funding)", "Custom criteria", "Manually verified", "Bulk export"],
    deliverables: ["Custom lead list", "Filter documentation", "Refresh schedule"],
  },
  {
    slug: "email-leads", title: "Email Leads", eyebrow: "Permission-based",
    icon: Mail,
    tagline: "Permission-based email lists for nurturing.",
    description: "Opt-in email lists built through lead magnets, landing pages and content offers — yours to nurture forever.",
    bullets: ["Lead magnet creation", "Landing page included", "Double opt-in", "GDPR/CAN-SPAM compliant", "ESP integration"],
    deliverables: ["Opt-in email list", "Lead magnet assets", "ESP setup"],
  },
  {
    slug: "call-selling", title: "Call Selling", eyebrow: "Closers on call",
    icon: Phone,
    tagline: "Trained closers selling your product over the phone.",
    description: "We train and deploy phone closers who sell your product — paid per call, per close, or hybrid.",
    bullets: ["Trained closing team", "Script + objection handling", "Call recording & QA", "CRM logging", "Performance reporting"],
    deliverables: ["Live calling team", "Call recordings", "Weekly performance report"],
  },
  {
    slug: "cold-calling", title: "Cold Calling", eyebrow: "Outbound dials",
    icon: Phone,
    tagline: "Compliant cold call campaigns with daily reporting.",
    description: "High-volume cold call campaigns — DNC checked, recorded for QA, and reported daily so you know exactly what's happening.",
    bullets: ["DNC list scrubbing", "Power dialer setup", "Recorded calls", "Daily activity reports", "Booked meeting handoff"],
    deliverables: ["Daily dial reports", "Call recordings", "Booked meetings"],
  },
  {
    slug: "appointment-setting", title: "Appointment Setting", eyebrow: "Calendar meetings",
    icon: CalendarDays,
    tagline: "Qualified meetings booked directly into your calendar.",
    description: "We book qualified discovery calls onto your calendar — you just show up and close.",
    bullets: ["Calendar integration", "Pre-qualification script", "Show-rate guarantee", "Confirmation sequences", "No-show recovery"],
    deliverables: ["Booked meetings", "Pre-call briefs", "Weekly pipeline report"],
  },
  {
    slug: "linkedin-leads", title: "LinkedIn Leads", eyebrow: "Sales Navigator",
    icon: AtSign,
    tagline: "LinkedIn outreach + Sales Navigator prospecting.",
    description: "Compliant LinkedIn outreach using Sales Navigator — connection campaigns, multi-touch messaging, InMail sequences.",
    bullets: ["Sales Navigator setup", "Connection campaigns", "Multi-touch sequences", "InMail credits usage", "Reply handling"],
    deliverables: ["Active LinkedIn campaign", "Reply tracker", "Weekly pipeline"],
  },
  {
    slug: "facebook-leads", title: "Facebook Leads", eyebrow: "Lead form ads",
    icon: Share2,
    tagline: "Lead form ads and Messenger qualification funnels.",
    description: "Facebook & Instagram lead-form campaigns with automated qualification, instant CRM sync and Messenger follow-up.",
    bullets: ["Lead form ad creative", "Audience targeting", "Messenger bot qualification", "Real-time CRM sync", "Lookalike audiences"],
    deliverables: ["Live lead campaigns", "Creative pack", "Daily lead feed"],
  },
  {
    slug: "whatsapp-marketing", title: "WhatsApp Marketing", eyebrow: "Business API",
    icon: MessageCircle,
    tagline: "Broadcast and 1:1 WhatsApp Business campaigns.",
    description: "WhatsApp Business API setup, template approvals, broadcast campaigns and chatbot flows.",
    bullets: ["WhatsApp Business API", "Template message approval", "Broadcast campaigns", "Chatbot flows", "CRM integration"],
    deliverables: ["WhatsApp API account", "Approved templates", "Campaign reports"],
  },
  {
    slug: "sms-marketing", title: "SMS Marketing", eyebrow: "Compliant SMS",
    icon: Phone,
    tagline: "Compliant SMS blasts and drip sequences.",
    description: "TCPA-compliant SMS marketing — opt-in collection, drip campaigns, broadcasts and 2-way conversations.",
    bullets: ["Opt-in collection", "Broadcast campaigns", "Drip sequences", "2-way reply handling", "TCPA compliance"],
    deliverables: ["Live SMS program", "Compliance documentation", "Weekly reports"],
  },
  {
    slug: "crm-management", title: "CRM Management", eyebrow: "HubSpot / Pipedrive / Zoho",
    icon: Briefcase,
    tagline: "HubSpot, Pipedrive and Zoho setup, hygiene and reporting.",
    description: "Full CRM ownership — setup, pipeline design, automation, data hygiene and reporting — so your team actually uses it.",
    bullets: ["CRM setup & migration", "Pipeline design", "Automation workflows", "Data hygiene", "Custom reporting"],
    deliverables: ["Configured CRM", "Automation library", "Monthly reports"],
  },
  {
    slug: "funnel-marketing", title: "Funnel Marketing", eyebrow: "Full funnel",
    icon: Funnel,
    tagline: "End-to-end funnel design, build and optimization.",
    description: "Top-to-bottom funnels — ads, landing pages, email sequences, upsells, downsells — optimized for ROAS.",
    bullets: ["Funnel strategy", "Landing page design", "Email sequences", "Upsell/downsell flows", "A/B testing"],
    deliverables: ["Live funnels", "Email sequences", "Weekly optimization"],
  },
  {
    slug: "landing-page-leads", title: "Landing Page Leads", eyebrow: "High conversion",
    icon: Layout,
    tagline: "High-converting landing pages with A/B testing.",
    description: "Custom landing pages built around your offer — copy, design, mobile optimization and continuous A/B testing.",
    bullets: ["Custom landing page design", "Conversion copywriting", "Mobile optimization", "A/B testing", "Heatmap analysis"],
    deliverables: ["Live landing pages", "A/B test reports", "Heatmap insights"],
  },
];

export const RELATED_LOCAL_SEO = LOCAL_SEO_SERVICES.map((s) => ({
  slug: s.slug, title: s.title, href: `/marketing/local-seo/${s.slug}`,
}));
export const RELATED_AMAZON = AMAZON_SERVICES.map((s) => ({
  slug: s.slug, title: s.title, href: `/marketing/amazon/${s.slug}`,
}));
export const RELATED_LEAD_GEN = LEAD_GEN_SERVICES.map((s) => ({
  slug: s.slug, title: s.title, href: `/marketing/lead-generation/${s.slug}`,
}));
